import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { CheckCircle2, Send } from 'lucide-react';
import type { ContactCategory, PublicContactMessageCreateInput } from '@forest/contracts';
import { Card, CardBody } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { FormField } from '@/components/ui/FormField';
import { api, apiError } from '@/lib/api-client';
import { useUiStore } from '@/stores/uiStore';

const LANGS: { key: 'ro' | 'ru' | 'en'; label: string }[] = [
  { key: 'ro', label: 'RO' },
  { key: 'ru', label: 'RU' },
  { key: 'en', label: 'EN' },
];

const CATEGORIES: ContactCategory[] = [
  'DELETE_ACCOUNT',
  'QUESTION',
  'SUPPORT',
  'FEEDBACK',
  'OTHER',
];

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function ContactPage() {
  const { t, i18n } = useTranslation(['common']);
  const pushToast = useUiStore((s) => s.pushToast);

  const [category, setCategory] = useState<ContactCategory>('QUESTION');
  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sent, setSent] = useState(false);

  const isDelete = category === 'DELETE_ACCOUNT';

  const submit = useMutation({
    mutationFn: (input: PublicContactMessageCreateInput) =>
      api.post('contact-messages/public', { json: input }).json(),
    onSuccess: () => setSent(true),
    onError: async (e) => {
      const err = await apiError(e);
      pushToast({ variant: 'error', message: err.message });
    },
  });

  const canSubmit =
    EMAIL_RE.test(email.trim()) &&
    title.trim().length >= 3 &&
    body.trim().length >= 10 &&
    (!isDelete || username.trim().length >= 1) &&
    !submit.isPending;

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!canSubmit) return;
    submit.mutate({
      category,
      contactEmail: email.trim(),
      title: title.trim(),
      body: body.trim(),
      ...(isDelete ? { requestedUsername: username.trim() } : {}),
    });
  };

  const categoryLabel = (c: ContactCategory) =>
    t(`contact.category.${c}`, { defaultValue: c });

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900">
      <header className="border-b border-slate-200 bg-white dark:border-slate-800 dark:bg-slate-950">
        <div className="mx-auto flex max-w-2xl items-center justify-between px-4 py-4">
          <Link to="/login" className="text-lg font-semibold text-forest-700 dark:text-forest-500">
            Pnerp
          </Link>
          <div className="flex gap-1">
            {LANGS.map((l) => (
              <button
                key={l.key}
                type="button"
                onClick={() => void i18n.changeLanguage(l.key)}
                className={`rounded px-2.5 py-1 text-sm font-medium transition ${
                  i18n.language?.startsWith(l.key)
                    ? 'bg-forest-700 text-white'
                    : 'text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-2xl px-4 py-8">
        <h1 className="text-2xl font-semibold text-slate-900 dark:text-slate-100">
          {t('contact.title', { defaultValue: 'Contact' })}
        </h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          {t('contact.subtitle', {
            defaultValue: 'Trimiteți un mesaj către Super Administrator.',
          })}
        </p>

        <Card className="mt-6">
          <CardBody>
            {sent ? (
              <div className="flex flex-col items-center gap-3 py-8 text-center">
                <CheckCircle2 className="h-10 w-10 text-forest-600 dark:text-forest-500" />
                <p className="text-sm text-slate-700 dark:text-slate-200">
                  {t('contact.success', {
                    defaultValue: 'Mesajul a fost trimis. Vă vom răspunde în curând.',
                  })}
                </p>
                <Link
                  to="/login"
                  className="text-sm font-medium text-forest-700 hover:underline dark:text-forest-500"
                >
                  {t('contact.backToLogin', { defaultValue: 'Înapoi la autentificare' })}
                </Link>
              </div>
            ) : (
              <form className="space-y-4" onSubmit={onSubmit} noValidate>
                <FormField
                  label={t('contact.category.label', { defaultValue: 'Motiv' })}
                  htmlFor="contact-category"
                >
                  <select
                    id="contact-category"
                    value={category}
                    onChange={(e) => setCategory(e.target.value as ContactCategory)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100"
                  >
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {categoryLabel(c)}
                      </option>
                    ))}
                  </select>
                </FormField>

                {isDelete ? (
                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-300">
                    {t('contact.deleteWarning', {
                      defaultValue:
                        'Ștergerea contului este o cerere procesată manual de Super Administrator.',
                    })}
                  </div>
                ) : null}

                {isDelete ? (
                  <FormField
                    label={t('contact.username.label', { defaultValue: 'Nume de utilizator' })}
                    htmlFor="contact-username"
                    required
                  >
                    <Input
                      id="contact-username"
                      value={username}
                      onChange={(e) => setUsername(e.target.value)}
                      maxLength={150}
                      placeholder={t('contact.username.placeholder', {
                        defaultValue: 'Numele contului de șters',
                      })}
                      autoComplete="username"
                    />
                  </FormField>
                ) : null}

                <FormField
                  label={t('contact.email.label', { defaultValue: 'Email de contact' })}
                  htmlFor="contact-email"
                  required
                >
                  <Input
                    id="contact-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    maxLength={255}
                    placeholder={t('contact.email.placeholder', { defaultValue: 'email@exemplu.md' })}
                    autoComplete="email"
                  />
                </FormField>

                <FormField
                  label={t('contact.subject.label', { defaultValue: 'Subiect' })}
                  htmlFor="contact-subject"
                  required
                >
                  <Input
                    id="contact-subject"
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    maxLength={200}
                    placeholder={t('contact.subject.placeholder', {
                      defaultValue: 'Pe scurt, despre ce este vorba?',
                    })}
                  />
                </FormField>

                <FormField
                  label={t('contact.message.label', { defaultValue: 'Mesaj' })}
                  htmlFor="contact-body"
                  required
                >
                  <textarea
                    id="contact-body"
                    value={body}
                    onChange={(e) => setBody(e.target.value)}
                    maxLength={5000}
                    rows={6}
                    placeholder={t('contact.message.placeholder', {
                      defaultValue: 'Detaliați cererea dumneavoastră.',
                    })}
                    className="w-full resize-y rounded-md border border-slate-300 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-forest-500 focus:outline-none focus:ring-1 focus:ring-forest-500 dark:border-slate-600 dark:bg-slate-900 dark:text-slate-100 dark:placeholder:text-slate-500"
                  />
                </FormField>

                <div className="flex items-center justify-between">
                  <Link
                    to="/login"
                    className="text-sm text-slate-500 hover:underline dark:text-slate-400"
                  >
                    {t('contact.backToLogin', { defaultValue: 'Înapoi la autentificare' })}
                  </Link>
                  <Button type="submit" loading={submit.isPending} disabled={!canSubmit}>
                    <Send className="mr-1 h-4 w-4" />
                    {t('contact.submit', { defaultValue: 'Trimite' })}
                  </Button>
                </div>
              </form>
            )}
          </CardBody>
        </Card>
      </main>
    </div>
  );
}
